const normalizeText = (value) =>
  typeof value === "string" ? value.trim() : value;

const isPersistedMessage = (message) => Boolean(message?._id);

const hasKeyValue = (value) => value !== undefined && value !== null && value !== "";

export const getStableMessageKey = (message) => {
  if (!message) return null;
  if (hasKeyValue(message.msgId)) return `msg:${String(message.msgId)}`;
  if (hasKeyValue(message.cliMsgId)) {
    const batchSuffix =
      message.type === "image" && message.batchIndex !== undefined
        ? `:batch:${String(message.batchIndex)}`
        : "";
    return `cli:${String(message.cliMsgId)}${batchSuffix}`;
  }
  if (hasKeyValue(message._id)) return `_id:${String(message._id)}`;
  return null;
};

const mergeDuplicateMessage = (existingMessage, incomingMessage) => {
  const resolvedByIncoming = Boolean(incomingMessage?._id || incomingMessage?.msgId);
  return {
    ...existingMessage,
    ...incomingMessage,
    previewSrc: incomingMessage.previewSrc || existingMessage.previewSrc,
    localPreviewUrl:
      incomingMessage.localPreviewUrl || existingMessage.localPreviewUrl,
    ...(resolvedByIncoming ? { isSending: false } : {}),
  };
};

export const dedupeMessagesByStableKey = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return Array.isArray(messages) ? messages : [];
  }

  const deduped = [];
  const indexByKey = new Map();
  let didChange = false;

  for (const message of messages) {
    if (!message) {
      didChange = true;
      continue;
    }

    const key = getStableMessageKey(message);
    if (!key) {
      deduped.push(message);
      continue;
    }

    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, deduped.length);
      deduped.push(message);
      continue;
    }

    deduped[existingIndex] = mergeDuplicateMessage(
      deduped[existingIndex],
      message,
    );
    didChange = true;
  }

  return didChange ? deduped : messages;
};

const getEffectivePreviewSrc = (message) =>
  message?.previewSrc || message?.localPreviewUrl || "";

const carryPreviewToIncomingMessage = (existingMessage, incomingMessage) => {
  if (incomingMessage?.type !== "image") return incomingMessage;

  const previewSrc = getEffectivePreviewSrc(existingMessage);
  if (!previewSrc) return incomingMessage;

  return {
    ...incomingMessage,
    previewSrc,
    localPreviewUrl: existingMessage.localPreviewUrl || previewSrc,
    batchIndex:
      existingMessage.batchIndex ?? incomingMessage.batchIndex ?? undefined,
  };
};

const canMatchSender = (existingMessage, incomingMessage) => {
  if (!existingMessage || !incomingMessage) return false;
  if (existingMessage.uidFrom === "0") return true;
  return incomingMessage.uidFrom === existingMessage.uidFrom;
};

const findMatchingIncomingIndex = (
  existingMessage,
  incomingMessages,
  matchedServerMsgIds,
) => {
  const findIndex = (predicate) =>
    incomingMessages.findIndex((message) => {
      if (!message) return false;
      if (matchedServerMsgIds.has(message._id)) return false;
      return predicate(message);
    });

  if (existingMessage?.msgId) {
    const byMsgId = findIndex(
      (message) =>
        message.msgId &&
        String(message.msgId) === String(existingMessage.msgId),
    );
    if (byMsgId >= 0) return byMsgId;
  }

  if (existingMessage?.cliMsgId) {
    const byCliMsgId = findIndex(
      (message) =>
        message.cliMsgId &&
        String(message.cliMsgId) === String(existingMessage.cliMsgId),
    );
    if (byCliMsgId >= 0) return byCliMsgId;
  }

  if (existingMessage?.type === "text") {
    return findIndex(
      (message) =>
        canMatchSender(existingMessage, message) &&
        normalizeText(message.content) === normalizeText(existingMessage.content),
    );
  }

  if (existingMessage?.type === "image") {
    return findIndex(
      (message) =>
        message.type === "image" && canMatchSender(existingMessage, message),
    );
  }

  if (existingMessage?.type === "file") {
    return findIndex(
      (message) =>
        message.type === "file" &&
        canMatchSender(existingMessage, message) &&
        (!existingMessage.content || message.content === existingMessage.content),
    );
  }

  return findIndex(
    (message) =>
      message.type === existingMessage?.type &&
      canMatchSender(existingMessage, message),
  );
};

export const buildOptimisticImageMessages = ({ files, cliMsgId, user }) =>
  files.map((file, index) => {
    const localPreviewUrl = URL.createObjectURL(file);
    return {
      type: "image",
      uidFrom: "0",
      img: localPreviewUrl,
      previewSrc: localPreviewUrl,
      localPreviewUrl,
      content: file.name,
      dateAction: new Date(),
      isSending: true,
      staffId: user._id,
      staffName: user.name,
      cliMsgId,
      batchIndex: index,
    };
  });

export const applyImageBatchAckToMessages = (
  messages,
  cliMsgId,
  { attachments = [], outboxId } = {},
) => {
  if (!Array.isArray(messages) || !cliMsgId) return messages;

  return messages.map((message) => {
    if (message?.cliMsgId !== cliMsgId || message?.type !== "image") {
      return message;
    }

    const attachment =
      message.batchIndex != null ? attachments[message.batchIndex] : undefined;

    return {
      ...message,
      isSending: false,
      sendingStatus: undefined,
      errorMessage: undefined,
      ...(attachment?.msgId != null ? { msgId: String(attachment.msgId) } : {}),
      ...(outboxId ? { outboxId } : {}),
    };
  });
};

export const mergeIncomingMessagesWithOptimistic = (
  prevMessages,
  incomingMessages,
  { onMatch } = {},
) => {
  if (!incomingMessages || incomingMessages.length === 0) {
    return dedupeMessagesByStableKey(prevMessages || []);
  }
  if (!prevMessages || prevMessages.length === 0) {
    return dedupeMessagesByStableKey(incomingMessages);
  }

  const matchedServerMsgIds = new Set();
  const clonedIncomingMessages = incomingMessages.map((message) => ({ ...message }));

  const filteredPrevMessages = prevMessages.filter((existingMessage) => {
    if (!existingMessage) return false;

    if (existingMessage.msgId && !isPersistedMessage(existingMessage)) {
      const matchedIncomingIndex = findMatchingIncomingIndex(
        existingMessage,
        clonedIncomingMessages,
        matchedServerMsgIds,
      );
      if (matchedIncomingIndex >= 0) {
        const matchedIncoming = carryPreviewToIncomingMessage(
          existingMessage,
          clonedIncomingMessages[matchedIncomingIndex],
        );
        clonedIncomingMessages[matchedIncomingIndex] = matchedIncoming;
        matchedServerMsgIds.add(matchedIncoming._id);
        onMatch?.(existingMessage, matchedIncoming);
        return false;
      }
    }

    if (!existingMessage.isSending && isPersistedMessage(existingMessage)) {
      return true;
    }

    const matchedIncomingIndex = findMatchingIncomingIndex(
      existingMessage,
      clonedIncomingMessages,
      matchedServerMsgIds,
    );

    if (matchedIncomingIndex < 0) return true;

    const matchedIncoming = carryPreviewToIncomingMessage(
      existingMessage,
      clonedIncomingMessages[matchedIncomingIndex],
    );
    clonedIncomingMessages[matchedIncomingIndex] = matchedIncoming;
    matchedServerMsgIds.add(matchedIncoming._id);
    onMatch?.(existingMessage, matchedIncoming);
    return false;
  });

  const mergedExistingMessages = filteredPrevMessages.map((existingMessage) => {
    const updatedMessage = clonedIncomingMessages.find(
      (message) => message._id && message._id === existingMessage._id,
    );
    return updatedMessage
      ? { ...existingMessage, ...updatedMessage, isSending: false }
      : existingMessage;
  });

  const existingIds = new Set(
    filteredPrevMessages.map((message) => message._id).filter(Boolean),
  );
  const brandNewMessages = clonedIncomingMessages.filter(
    (message) => !existingIds.has(message._id),
  );

  return dedupeMessagesByStableKey([
    ...mergedExistingMessages,
    ...brandNewMessages,
  ]);
};
